import { useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useNavigate } from 'react-router-dom';
import { fetchDashboards } from '../../../../../redux/slices/monitoringDashboardsSlice';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: any): ActionEntity => ({ type: 'monitoring_dashboard', id: row.dashboard_id });

const ListMonitoringDashboards = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { dashboards, config: headerConfig, filtersConfig, pagination, status, error, loading } = useAppSelector((state) => state.monitoringDashboards);

  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'monitoring_dashboards';
  const [showSidebar, setShowSidebar] = useState(false);

  const isLoading = loading.fetchDashboards;

  useEffect(() => {
    if (status === null) {
      dispatch(fetchDashboards({}) as any);
    }
  }, [dispatch, status]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: fetchDashboards,
    columnFormats: { created_at: 'datetime', updated_at: 'datetime' },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh, navigate });
      return <ActionDropdown actions={actions} items={['view', 'edit', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, folderColumn, actionsColumn];

  const formattedData = useMemo(() => {
    return getFormattedData(dashboards || []);
  }, [dashboards]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: false,
  });

  const { currentPage, totalPages, totalItems } = pagination;

  const toolbarActions = getActions({ entities: [], refresh, navigate });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={(headerConfig ?? []).map((c: any) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={<ActionButton actions={toolbarActions} action="createDashboard" variant="primary" />}
    />
  );
};

export default ListMonitoringDashboards;
